/**
 * ThemeContext — Light / Dark / System theme management.
 *
 * - Detects system preference via `prefers-color-scheme`
 * - Persists user choice in localStorage
 * - Applies/removes the `dark` class on <html> for Tailwind + CSS variables
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'nannyou-theme';

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'system';
    } catch (err) {
      console.warn('ThemeContext: localStorage read failed', err);
      return 'system';
    }
  });

  // Resolved theme = what's actually applied (light or dark)
  const [resolved, setResolved] = useState('light');

  const applyTheme = useCallback((pref) => {
    let theme;
    if (pref === 'system') {
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      theme = pref;
    }
    setResolved(theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, []);

  // Apply on mount + when preference changes
  useEffect(() => {
    applyTheme(preference);
  }, [preference, applyTheme]);

  // Listen to system theme changes (only relevant when preference is 'system')
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (preference === 'system') {
        applyTheme('system');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference, applyTheme]);

  const setTheme = useCallback((newPref) => {
    setPreference(newPref);
    try {
      localStorage.setItem(STORAGE_KEY, newPref);
    } catch (err) { console.warn('ThemeContext: localStorage write failed', err); }
  }, []);

  const value = useMemo(() => ({
    theme: preference,      // 'light' | 'dark' | 'system'
    resolved,               // 'light' | 'dark' (what's actually applied)
    setTheme,
  }), [preference, resolved, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback for components outside provider (e.g., Sonner)
    return { theme: 'system', resolved: 'light', setTheme: () => {} };
  }
  return context;
};
